import AddIcon from '@mui/icons-material/Add'
import {
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardContentProps,
  CardProps,
  IconButton,
  Stack,
  styled,
  Typography,
  TypographyProps,
} from '@mui/material'

import Decimal from 'decimal.js'
import { FC, useContext, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { TrailSystem } from '../../models'
import { TrailSystemContext } from '../../providers'
import { getMostValidSponsor } from '../../utils'
import { TrailSystemStatus } from '../index'

const StyledCard = styled(Card)<CardProps>({
  '& .MuiCardActionArea-root': {
    height: '100%',
  },
  height: '100%',
})

const StyledCardContent = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== 'mini',
})<CardContentProps & { mini?: boolean }>(({ mini }) => ({
  '&:last-child': {
    paddingBottom: mini ? 8 : undefined,
  },
  padding: mini ? 8 : undefined,
  paddingBottom: 0,
}))

const SponsorLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'mini',
})<TypographyProps & { mini?: boolean }>(({ theme, mini }) => ({
  color: theme.palette.grey[400],
  fontSize: mini ? 12 : 16,
  textAlign: 'center',
  whiteSpace: 'normal',
}))

type TrailSystemCardProps = {
  trailSystem: TrailSystem
  mini?: boolean
  embedded?: boolean
}

export const TrailSystemCard: FC<TrailSystemCardProps> = (props) => {
  const location = useLocation()
  const { anyFreezeThawActive } = useContext(TrailSystemContext)

  // Get the most valid sponsor for display on the card
  const displaySponsor = useMemo(() => {
    // First try to use the new sponsors array if available
    if (props.trailSystem.sponsors && props.trailSystem.sponsors.length > 0) {
      return getMostValidSponsor(props.trailSystem.sponsors)
    }
    // Fall back to the existing sponsor field for backwards compatibility
    return props.trailSystem.sponsor
  }, [props.trailSystem.sponsors, props.trailSystem.sponsor])

  const trailStats = useMemo(() => {
    const initialStats = {
      closed: 0,
      count: 0,
      highImpact: 0,
      lowImpact: 0,
      mediumImpact: 0,
      miles: 0,
    }
    return props.trailSystem.trails
      ? props.trailSystem.trails.reduce(
          (acc, trail) => ({
            closed: new Decimal(acc.closed).add(trail.status?.status === 0 ? 1 : 0).toNumber(),
            count: new Decimal(acc.count).add(1).toNumber(),
            highImpact: new Decimal(acc.highImpact).add(trail.status?.status === 1 ? 1 : 0).toNumber(),
            lowImpact: new Decimal(acc.lowImpact).add(trail.status?.status === 3 ? 1 : 0).toNumber(),
            mediumImpact: new Decimal(acc.mediumImpact).add(trail.status?.status === 2 ? 1 : 0).toNumber(),
            miles: new Decimal(acc.miles).add(trail.distance).toDecimalPlaces(1).toNumber(),
          }),
          initialStats,
        )
      : initialStats
  }, [props.trailSystem.trails])

  const trailFreezeThawStats = useMemo(() => {
    const initialStats = {
      closed: 0,
      count: 0,
      highImpact: 0,
      lowImpact: 0,
      mediumImpact: 0,
      miles: 0,
    }
    return props.trailSystem.trails
      ? props.trailSystem.trails.reduce((acc, trail) => {
          const isFreezeThaw = trail.status?.freezeThawStatus?.status && trail.status?.freezeThawStatus?.status > 0
          return {
            closed: new Decimal(acc.closed).add(isFreezeThaw && trail.status?.status === 0 ? 1 : 0).toNumber(),
            count: new Decimal(acc.count).add(isFreezeThaw ? 1 : 0).toNumber(),
            highImpact: new Decimal(acc.highImpact).add(isFreezeThaw && trail.status?.status === 1 ? 1 : 0).toNumber(),
            lowImpact: new Decimal(acc.lowImpact).add(isFreezeThaw && trail.status?.status === 3 ? 1 : 0).toNumber(),
            mediumImpact: new Decimal(acc.mediumImpact)
              .add(isFreezeThaw && trail.status?.status === 2 ? 1 : 0)
              .toNumber(),
            miles: new Decimal(acc.miles)
              .add(isFreezeThaw ? trail.distance : 0)
              .toDecimalPlaces(1)
              .toNumber(),
          }
        }, initialStats)
      : initialStats
  }, [props.trailSystem.trails])

  return (
    <StyledCard>
      <CardActionArea
        href={`/trail-system/${props.trailSystem.permalink}${location.search}`}
        target={props.embedded ? '_blank' : undefined}
      >
        <Stack justifyContent="space-between" width="100%" height="100%">
          <StyledCardContent mini={props.mini}>
            <Stack alignItems="center" spacing={props.mini ? 1 : 2}>
              <Stack alignItems="stretch" width="100%">
                <Stack>
                  <Typography component="h2" variant="h5" fontWeight={700} fontSize={props.mini ? 16 : 24}>
                    {props.trailSystem.name}
                  </Typography>
                  <Typography component="h3" variant="subtitle2" fontSize={props.mini ? 12 : 14}>
                    {trailStats.miles} miles of trails
                  </Typography>
                </Stack>
              </Stack>
              <TrailSystemStatus
                values={{
                  closed: trailStats.closed,
                  highImpact: trailStats.highImpact - trailFreezeThawStats.highImpact,
                  lowImpact: trailStats.lowImpact - trailFreezeThawStats.lowImpact,
                  mediumImpact: trailStats.mediumImpact - trailFreezeThawStats.mediumImpact,
                  freezeThaw: trailFreezeThawStats.count,
                }}
                // test values to show all rings full
                // values={{
                //   closed: 5,
                //   mediumImpact: 5,
                //   lowImpact: 5,
                //   highImpact: 5,
                // }}
                count={trailStats.count}
                mini={props.mini}
                showFreezeThawRing={anyFreezeThawActive}
              />
              {!!displaySponsor && (
                <Stack alignItems="center" justifyContent="center" width="100%">
                  <SponsorLabel mini={props.mini}>Powered by {displaySponsor.name}</SponsorLabel>
                </Stack>
              )}
            </Stack>
          </StyledCardContent>
          {!props.mini && (
            <CardActions>
              <Stack direction="row" justifyContent="center" width="100%">
                <IconButton aria-label="View Trail System" size="medium">
                  <AddIcon fontSize="inherit" />
                </IconButton>
              </Stack>
            </CardActions>
          )}
        </Stack>
      </CardActionArea>
    </StyledCard>
  )
}
TrailSystemCard.displayName = 'TrailSystemCard'
